import { IUser } from '@clickster/constant-definitions/dist-esm/entities/user/types';
import { API_V2 } from '../lib/axios';
import { fetchAllPaginated } from './utils/fetch-all-paginated';

const USER_ENDPOINT = '/users';


const list = async (params = {}) => {
  return fetchAllPaginated<IUser>(API_V2, USER_ENDPOINT, params);
};

const find = async (id: string) => {
  const response = await API_V2.get<IUser>(`${USER_ENDPOINT}/${id}`);

  return response.data;
};

const update = async (data: IUser) => {
  const response = await API_V2.put<IUser>(`${USER_ENDPOINT}/${data._id}`, data);

  return response.data;
};

const create = async (data: IUser) => {
  if (data.credentials && !data.credentials.current && data.credentials.newPassword) {
    data.credentials.current = data.credentials.newPassword;
  }

  const response = await API_V2.post<IUser>(`${USER_ENDPOINT}`, data);

  return response.data;
};

/**
 * Fetch the current user's information from the API
 * @returns The user data
 */
const getUserMe = async (): Promise<IUser | null> => {
  try {
    const response = await API_V2.get<IUser>(`${USER_ENDPOINT}/me`);

    if (response.data) {
      // Set user timezone if not already set
      const user = response.data;
      if (!user.timeZone) {
        user.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }
      return user;
    }

    return null;
  } catch (error) {
    console.error('Error fetching user data', error);
    return null;
  }
};

/**
 * Toggle a campaign in the user's favorites (add or remove)
 * @param userId - The ID of the user
 * @param campaignId - The ID of the campaign to toggle in favorites
 * @param isFavorite - If true, add to favorites; if false, remove from favorites
 * @returns The updated user data
 */
const toggleFavoriteCampaign = async (
  userId: string,
  campaignId: string,
  isFavorite: boolean,
): Promise<IUser | null> => {
  try {
    // Fetch current user to get their favorites
    const user = await find(userId);

    if (!user) return null;

    // Create proper structure if not exists
    const favorites = user.favorites || {};
    const campaigns = Array.isArray(favorites.campaigns) ? [...favorites.campaigns] : [];

    let updatedCampaigns;
    if (isFavorite) {
      // Add to favorites (if not already there)
      if (!campaigns.includes(campaignId)) {
        updatedCampaigns = [...campaigns, campaignId];
      } else {
        updatedCampaigns = campaigns; // No change needed
      }
    } else {
      // Remove from favorites
      updatedCampaigns = campaigns.filter((id) => {
        // Handle both string and ObjectId comparison
        if (typeof id === 'string') {
          return id !== campaignId;
        } else if (id.toString) {
          // For ObjectId, compare string representation
          return id.toString() !== campaignId;
        }
        return true; // Keep any other type (shouldn't happen in practice)
      });
    }

    // Update user with new favorites
    const updatedUser = {
      ...user,
      favorites: {
        ...favorites,
        campaigns: updatedCampaigns,
      },
    };

    // Save the updated user
    const result = await update(updatedUser);
    return result;
  } catch (error) {
    console.error('Error toggling favorite campaign:', error);
    return null;
  }
};

/**
 * Remove a campaign from the user's favorites
 * @param userId - The ID of the user
 * @param campaignId - The ID of the campaign to remove from favorites
 * @returns The updated user data
 */
const removeFavoriteCampaign = async (userId: string, campaignId: string): Promise<IUser | null> => {
  return toggleFavoriteCampaign(userId, campaignId, false);
};

/**
 * Delete users by IDs (logical deletion - sets status to 'deleted')
 * @param ids - Array of user IDs to delete
 * @returns Response from the API
 * @throws Error if the request fails
 */
const remove = async (ids: string[]) => {
  try {
    const response = await API_V2.delete(`${USER_ENDPOINT}/many`, {
      params: { ids },
      paramsSerializer: (params) => {
        // Serialize arrays without brackets: ids=1&ids=2 instead of ids[]=1&ids[]=2
        const parts: string[] = [];
        Object.keys(params).forEach((key) => {
          const value = params[key];
          if (Array.isArray(value)) {
            value.forEach((val) => {
              parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(val)}`);
            });
          } else {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
          }
        });
        return parts.join('&');
      },
    });

    return response.data;
  } catch (error: any) {
    console.error('UserService.remove error:', error?.response?.data || error?.message);
    throw error; // Re-throw to let React Query handle it
  }
};


export const UserService = {
  list,
  find,
  update,
  create,
  getUserMe,
  toggleFavoriteCampaign,
  removeFavoriteCampaign,
  remove,
};
